// ─── storage.js ───────────────────────────────────────────────────────────────
import { supabase } from "./supabase";
import { markSetting } from "./settings";
import { enqueue, flushOutbox, pendingIds } from "./outbox";

const isCapacitor = () =>
  typeof window !== "undefined" &&
  window.Capacitor !== undefined &&
  window.Capacitor.isNativePlatform();

export const convertToPlayableUri = (localPath) => {
  if (!localPath) return null;
  if (isCapacitor() && window.Capacitor?.convertFileSrc) {
    return window.Capacitor.convertFileSrc(localPath);
  }
  return localPath;
};

let _plugins = null;
const getPlugins = async () => {
  if (!isCapacitor()) return null;
  if (_plugins) return _plugins;
  try {
    const { CapacitorSQLite } = await import("@capacitor-community/sqlite");
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Network } = await import("@capacitor/network");
    _plugins = { sqlite: CapacitorSQLite, Filesystem, Directory, Network };
    return _plugins;
  } catch (e) { console.warn("Capacitor plugins not available:", e); return null; }
};

export const isOnline = async () => {
  const p = await getPlugins();
  if (p) { try { const s = await p.Network.getStatus(); return s.connected; } catch {} }
  return navigator.onLine;
};

let _db = null, _dbInitPromise = null;
const initDB = async () => {
  if (_db) return _db;
  if (_dbInitPromise) return _dbInitPromise;
  _dbInitPromise = (async () => {
    const p = await getPlugins();
    if (!p) return null;
    const { sqlite } = p;
    try {
      for (let i = 0; i < 15; i++) {
        try { await sqlite.isDatabase({ database: "audiocards" }); break; }
        catch { await new Promise(r => setTimeout(r, 300)); }
      }
      // После перезагрузки страницы (pull-to-refresh, обновление приложения) нативное
      // соединение остаётся живым, и createConnection отвечает «already exists» —
      // тогда берём его как есть. (retrieveConnection тут не подходит: это метод
      // обёртки SQLiteConnection, у самого плагина его нет.)
      let reused = false;
      try { await sqlite.createConnection({ database: "audiocards", encrypted: false, mode: "no-encryption", version: 1 }); }
      catch (e) { if (!/already exists/i.test(String(e?.message || e))) throw e; reused = true; }
      let open = false;
      if (reused) { try { open = !!(await sqlite.isDBOpen({ database: "audiocards" }))?.result; } catch {} }
      if (!open) await sqlite.open({ database: "audiocards" });
      await sqlite.execute({ database: "audiocards", statements: `
        CREATE TABLE IF NOT EXISTS projects (
          id TEXT PRIMARY KEY, user_id TEXT, name TEXT NOT NULL,
          audio_path TEXT, folder TEXT, local_audio_filename TEXT,
          src_lang TEXT,
          created_at TEXT, updated_at TEXT, synced INTEGER DEFAULT 0
        );
        CREATE TABLE IF NOT EXISTS cards (
          id TEXT PRIMARY KEY, project_id TEXT, time_sec REAL,
          word TEXT, translation TEXT, color TEXT,
          srs_state TEXT, srs_interval REAL, srs_ease REAL, srs_due TEXT,
          srs_stability REAL, srs_difficulty REAL,
          srs_reps INTEGER DEFAULT 0, srs_lapses INTEGER DEFAULT 0,
          srs_last_review TEXT
        );` });
      // Миграции: добавить новые колонки в существующие таблицы — только недостающие.
      // (Раньше ALTER шёл каждый запуск и падал на уже существующих колонках: в журнал
      // ошибок сыпались сотни «duplicate column name».)
      const columns = async (table) => {
        try {
          const r = await sqlite.query({ database: "audiocards", statement: `PRAGMA table_info(${table});`, values: [] });
          return new Set((r?.values || []).map((c) => c.name));
        } catch { return null; }
      };
      const addMissing = async (table, cols) => {
        const have = await columns(table);
        for (const col of cols) {
          if (have && have.has(col.split(" ")[0])) continue;
          try { await sqlite.execute({ database: "audiocards", statements: `ALTER TABLE ${table} ADD COLUMN ${col};` }); } catch {}
        }
      };
      await addMissing("projects", [`src_lang TEXT`]);
      await addMissing("cards", [
        `srs_state TEXT`, `srs_interval REAL`, `srs_ease REAL`, `srs_due TEXT`,
        `srs_stability REAL`, `srs_difficulty REAL`,
        `srs_reps INTEGER DEFAULT 0`, `srs_lapses INTEGER DEFAULT 0`, `srs_last_review TEXT`,
      ]);
      _db = { name: "audiocards", sqlite };
      return _db;
    } catch (e) { console.error("[DB] init error:", e); _dbInitPromise = null; return null; }
  })();
  return _dbInitPromise;
};

const LS_KEY = "ac_projects_v2";
const lsLoad = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const lsSave = (d) => { try { localStorage.setItem(LS_KEY, JSON.stringify(d)); } catch {} };
const lsUpsert = (project, cards) => {
  const all = lsLoad();
  const proj = { ...project, cards };
  const idx = all.findIndex(p => p.id === project.id);
  if (idx >= 0) all[idx] = proj; else all.unshift(proj);
  lsSave(all);
};

// ─── БЫСТРЫЙ КЭШ КАРТОЧЕК (отдельный ключ на каждый проект) ─────────────────
// Позволяет мгновенно открыть проект без полной загрузки всей библиотеки
const QK = (id) => `ac_qc_${id}`;
export const getProjectQuickCache = (projectId) => {
  try { const r = localStorage.getItem(QK(projectId)); return r ? JSON.parse(r) : null; }
  catch { return null; }
};
const setProjectQuickCache = (project, cards) => {
  try { localStorage.setItem(QK(project.id), JSON.stringify({ ...project, cards, _cachedAt: Date.now() })); }
  catch {}
};
export const deleteProjectQuickCache = (projectId) => {
  try { localStorage.removeItem(QK(projectId)); } catch {}
};

// ─── ПАПКИ ───────────────────────────────────────────────────────────────────
export const loadLocalFolders = (userId) => {
  try { const r = localStorage.getItem(`ac_folders_${userId}`); return r ? JSON.parse(r) : []; }
  catch { return []; }
};
export const saveLocalFolder = (userId, name) => {
  try {
    const f = loadLocalFolders(userId);
    if (!f.includes(name)) {
      localStorage.setItem(`ac_folders_${userId}`, JSON.stringify([...f, name]));
      markSetting(`ac_folders_${userId}`);
    }
  } catch {}
};
export const deleteLocalFolder = (userId, name) => {
  try {
    localStorage.setItem(`ac_folders_${userId}`, JSON.stringify(loadLocalFolders(userId).filter(f => f !== name)));
    markSetting(`ac_folders_${userId}`);
  } catch {}
};

// ─── ПРОЕКТЫ ─────────────────────────────────────────────────────────────────
export const loadLocalProjects = async (userId) => {
  if (isCapacitor()) {
    try {
      const db = await initDB();
      if (db) {
        const pr = await db.sqlite.query({ database: db.name, statement: "SELECT * FROM projects WHERE user_id = ? ORDER BY updated_at DESC", values: [userId] });
        const projs = pr.values || [];
        if (projs.length > 0) {
          const cr = await db.sqlite.query({ database: db.name, statement: "SELECT * FROM cards", values: [] });
          const byProj = {};
          (cr.values || []).forEach(c => { if (!byProj[c.project_id]) byProj[c.project_id] = []; byProj[c.project_id].push({ ...c, time_sec: parseFloat(c.time_sec) }); });
          return projs.map(p => ({ ...p, cards: byProj[p.id] || [], cardCount: (byProj[p.id] || []).length }));
        }
      }
    } catch (e) { console.error("[DB] loadLocalProjects:", e); }
  }
  return lsLoad().filter(p => p.user_id === userId || !p.user_id);
};

export const saveProjectLocal = async (project, cards) => {
  lsUpsert(project, cards);
  setProjectQuickCache(project, cards); // быстрый кэш
  if (!isCapacitor()) return;
  try {
    const db = await initDB();
    if (!db) return;
    await db.sqlite.run({ database: db.name,
      statement: `INSERT OR REPLACE INTO projects (id,user_id,name,audio_path,folder,local_audio_filename,src_lang,created_at,updated_at,synced) VALUES (?,?,?,?,?,?,?,?,?,?)`,
      values: [project.id, project.user_id, project.name, project.audio_path||null, project.folder||null, project.local_audio_filename||null, project.src_lang||null, project.created_at||new Date().toISOString(), project.updated_at||new Date().toISOString(), project.synced??0] });
    for (const c of cards) {
      await db.sqlite.run({ database: db.name,
        statement: `INSERT OR REPLACE INTO cards (id,project_id,time_sec,word,translation,color,srs_state,srs_interval,srs_ease,srs_due,srs_stability,srs_difficulty,srs_reps,srs_lapses,srs_last_review) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
        values: [
          String(c.id), project.id, c.time_sec, c.word, c.translation||"", c.color||"",
          c.srs_state||"new",
          Number.isFinite(c.srs_interval) ? c.srs_interval : 0,
          Number.isFinite(c.srs_ease)     ? c.srs_ease     : 2.5,
          c.srs_due||null,
          Number.isFinite(c.srs_stability)  ? c.srs_stability  : null,
          Number.isFinite(c.srs_difficulty) ? c.srs_difficulty : null,
          c.srs_reps   || 0,
          c.srs_lapses || 0,
          c.srs_last_review || null,
        ] });
    }
    if (cards.length > 0) {
      const ids = cards.map(c => `'${String(c.id)}'`).join(",");
      await db.sqlite.execute({ database: db.name, statements: `DELETE FROM cards WHERE project_id='${project.id}' AND id NOT IN (${ids})` });
    } else {
      await db.sqlite.run({ database: db.name, statement: "DELETE FROM cards WHERE project_id=?", values: [project.id] });
    }
  } catch (e) { console.error("[DB] saveProjectLocal:", e); }
};

const WEB_AUDIO_CACHE = "audiocards-audio-v1";
const WEB_STATUS_KEY = "ac_offline_audio";
const webDlStatus = () => { try { return JSON.parse(localStorage.getItem(WEB_STATUS_KEY)||"{}"); } catch { return {}; } };
const webDlSave = (d) => { try { localStorage.setItem(WEB_STATUS_KEY, JSON.stringify(d)); } catch {} };

// Проверяет реальное наличие файла в Cache API (не только localStorage-флаг).
// Если кэш был очищен браузером — синхронизирует localStorage и возвращает false.
export const checkWebAudioDownloaded = async (projectId) => {
  if (isCapacitor()) return false;
  const st = webDlStatus()[projectId];
  if (!st) return false;
  if (!('caches' in window)) return true; // кэш API недоступен — верим localStorage
  try {
    const cache = await caches.open(WEB_AUDIO_CACHE);
    const keys = await cache.keys();
    const exists = keys.some(k => k.url.includes(`/offline-audio/${projectId}`));
    if (!exists) {
      // Кэш был очищен — чистим и localStorage
      const all = webDlStatus();
      delete all[projectId];
      webDlSave(all);
      return false;
    }
    return true;
  } catch { return true; }
};

// Синхронизирует статусы всех проектов с реальным Cache API.
// Вызывается при старте приложения чтобы галочки не «зависали» после очистки кэша.
export const syncWebAudioStatus = async (projectIds) => {
  if (isCapacitor() || !('caches' in window)) return {};
  try {
    const cache = await caches.open(WEB_AUDIO_CACHE);
    const keys = await cache.keys();
    const cachedUrls = new Set(keys.map(k => k.url));
    const st = webDlStatus();
    let changed = false;
    const result = {};
    for (const id of projectIds) {
      const entry = st[id];
      if (entry) {
        const inCache = cachedUrls.has(entry.url) ||
          [...cachedUrls].some(u => u.includes(`/offline-audio/${id}`));
        if (inCache) { result[id] = "done"; }
        else { delete st[id]; changed = true; result[id] = "idle"; }
      }
    }
    if (changed) webDlSave(st);
    return result; // {projectId: "done"|"idle"}
  } catch { return {}; }
};

export const downloadWebAudio = async (projectId, publicUrl, onProgress) => {
  if (isCapacitor() || !("caches" in window)) return false;
  try {
    onProgress(0);
    const response = await fetch(publicUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const contentLength = response.headers.get("content-length");
    const total = contentLength ? parseInt(contentLength) : 0;
    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (total > 0) onProgress(Math.min(99, Math.round((received / total) * 100)));
    }
    const blob = new Blob(chunks);
    const cache = await caches.open(WEB_AUDIO_CACHE);
    const ext = (publicUrl.split(".").pop().split("?")[0] || "mp3").toLowerCase().substring(0, 4);
    await cache.put(`/offline-audio/${projectId}.${ext}`, new Response(blob, { headers: { "Content-Type": `audio/${ext}` } }));
    const st = webDlStatus();
    st[projectId] = { ext, url: `/offline-audio/${projectId}.${ext}`, size: blob.size };
    webDlSave(st);
    onProgress(100);
    return true;
  } catch (e) { console.error("[WebAudio] download:", e); onProgress(null); return false; }
};

export const getWebAudioCacheUrl = (projectId) => {
  if (isCapacitor()) return null;
  const st = webDlStatus()[projectId];
  return st ? st.url : null;
};

export const deleteWebAudio = async (projectId) => {
  if (isCapacitor() || !("caches" in window)) return;
  try {
    const cache = await caches.open(WEB_AUDIO_CACHE);
    const keys = await cache.keys();
    await Promise.all(keys.filter(k => k.url.includes(`/offline-audio/${projectId}`)).map(k => cache.delete(k)));
    const st = webDlStatus();
    delete st[projectId];
    webDlSave(st);
  } catch (e) { console.error("[WebAudio] delete:", e); }
};

export const deleteProjectLocal = async (projectId) => {
  lsSave(lsLoad().filter(p => p.id !== projectId));
  deleteProjectQuickCache(projectId);
  if (!isCapacitor()) return;
  try {
    const db = await initDB();
    if (!db) return;
    await db.sqlite.run({ database: db.name, statement: "DELETE FROM projects WHERE id=?", values: [projectId] });
    await db.sqlite.run({ database: db.name, statement: "DELETE FROM cards WHERE project_id=?", values: [projectId] });
  } catch (e) { console.error("[DB] deleteProjectLocal:", e); }
};

// ─── АУДИО ────────────────────────────────────────────────────────────────────
// Удаление нативного аудиофайла с устройства (Android/iOS)
export const deleteNativeAudio = async (localAudioFilename) => {
  if (!isCapacitor() || !localAudioFilename) return false;
  const p = await getPlugins();
  if (!p) return false;
  const { Filesystem, Directory } = p;
  try {
    await Filesystem.deleteFile({ path: localAudioFilename, directory: Directory.Data });
    return true;
  } catch (e) {
    console.warn("[Audio] deleteNativeAudio:", e);
    return false;
  }
};

// Проверка свободного места на устройстве перед скачиванием
// Возвращает { ok: bool, freeBytes: number|null, fileSize: number|null }
export const checkStorageBeforeDownload = async (publicUrl) => {
  let fileSize = null;
  try {
    const r = await fetch(publicUrl, { method: "HEAD" });
    const cl = r.headers.get("content-length");
    if (cl) fileSize = parseInt(cl);
  } catch {}

  const RESERVE = 50 * 1024 * 1024; // 50 MB запас
  const needed = (fileSize || 0) + RESERVE;

  // Web: используем Storage API
  if (!isCapacitor() && "storage" in navigator) {
    try {
      const est = await navigator.storage.estimate();
      const free = (est.quota || 0) - (est.usage || 0);
      if (free > 0 && free < needed) return { ok: false, freeBytes: free, fileSize };
    } catch {}
  }

  // Native: пробуем через Filesystem — записываем 1-байтовый тест
  if (isCapacitor()) {
    const p = await getPlugins();
    if (p) {
      try {
        const { Filesystem, Directory } = p;
        await Filesystem.writeFile({ path: "_space_check.tmp", data: "x", directory: Directory.Data });
        await Filesystem.deleteFile({ path: "_space_check.tmp", directory: Directory.Data });
      } catch (e) {
        if (e.message?.includes("space") || e.message?.includes("storage")) {
          return { ok: false, freeBytes: null, fileSize };
        }
      }
    }
  }

  return { ok: true, freeBytes: null, fileSize };
};

export const checkAudioDownloaded = async (projectId, audioPath) => {
  if (!isCapacitor()) return false;
  const p = await getPlugins();
  if (!p) return false;
  const { Filesystem, Directory } = p;
  const ext = (audioPath?.split(".").pop()?.split("?")[0] || "mp3").toLowerCase().substring(0, 4);
  const fileName = `audio_${projectId}.${ext}`;
  try {
    const st = await Filesystem.stat({ path: fileName, directory: Directory.Data });
    // Пустой файл = прерванное скачивание; считаем что не скачано чтобы
    // ensureAudioDownloaded заново попробовал.
    if (!st || st.size === 0) return false;
    return fileName;
  } catch { return false; }
};

// Скачивание с прогрессом (как в подкаст-приложениях)
// onProgress(0..100) = прогресс, onProgress(null) = ошибка
export const downloadAudioWithProgress = async (projectId, publicUrl, onProgress) => {
  if (!isCapacitor()) return null;
  const p = await getPlugins();
  if (!p) return null;
  const { Filesystem, Directory } = p;

  const ext = (publicUrl.split(".").pop().split("?")[0] || "mp3").toLowerCase().substring(0, 4);
  const fileName = `audio_${projectId}.${ext}`;

  try {
    onProgress(0);
    const response = await fetch(publicUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const contentLength = response.headers.get("content-length");
    const total = contentLength ? parseInt(contentLength) : 0;
    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (total > 0) onProgress(Math.min(99, Math.round((received / total) * 100)));
    }

    // Blob → base64 через FileReader (не ломается на больших файлах)
    const blob = new Blob(chunks);
    const base64 = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result.split(",")[1]);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });

    await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Data });
    // Verify: сравниваем фактический размер с ожидаемым из Content-Length.
    // Если меньше — запись провалилась/обрезалась; удаляем огрызок, чтобы
    // checkAudioDownloaded не показывал ложную галочку «скачано».
    try {
      const st = await Filesystem.stat({ path: fileName, directory: Directory.Data });
      if (total > 0 && st.size < total * 0.95) {
        console.warn(`[Audio] partial write: got ${st.size}/${total} bytes — discarding`);
        try { await Filesystem.deleteFile({ path: fileName, directory: Directory.Data }); } catch {}
        onProgress(null);
        return null;
      }
    } catch {}
    onProgress(100);
    return fileName;
  } catch (e) {
    console.error("[Audio] downloadAudioWithProgress:", e);
    // Чистим возможный огрызок чтобы не висел как «скачано» в checkAudioDownloaded
    try { await Filesystem.deleteFile({ path: fileName, directory: Directory.Data }); } catch {}
    onProgress(null);
    return null;
  }
};

export const ensureAudioDownloaded = async (projectId, publicUrl) => {
  if (!isCapacitor()) return null;
  const p = await getPlugins();
  if (!p) return null;
  const { Filesystem, Directory } = p;
  const ext = (publicUrl.split(".").pop().split("?")[0] || "mp3").toLowerCase().substring(0, 4);
  const fileName = `audio_${projectId}.${ext}`;
  try { await Filesystem.stat({ path: fileName, directory: Directory.Data }); return fileName; } catch {}
  try { await Filesystem.downloadFile({ url: publicUrl, path: fileName, directory: Directory.Data }); return fileName; }
  catch (e) { console.error("[Audio] ensureAudioDownloaded:", e); return null; }
};

export const getPlayableAudioUri = async (localAudioFilename) => {
  if (!localAudioFilename || !isCapacitor()) return null;
  const p = await getPlugins();
  if (!p) return null;
  const { Filesystem, Directory } = p;
  try {
    // ВАЖНО: stat() прежде чем вернуть URI. Раньше getUri возвращал URL
    // без проверки существования файла — если юзер очистил данные приложения
    // через системные настройки, или файл побит после прерванного скачивания,
    // URI выглядел валидным, аудио тег пытался играть → silent fail без fallback
    // на стриминг. Теперь возвращаем null если файла нет → caller (App._loadAudioForProj)
    // правильно фолбэкнется на public URL.
    const st = await Filesystem.stat({ path: localAudioFilename, directory: Directory.Data });
    if (!st || st.size === 0) {
      console.warn("[Audio] local file empty:", localAudioFilename);
      return null;
    }
    const r = await Filesystem.getUri({ path: localAudioFilename, directory: Directory.Data });
    return convertToPlayableUri(r.uri);
  } catch (e) {
    // stat выкидывает ошибку если файла нет — это норма после очистки данных
    // или удаления через настройки Android. Не console.error, чтобы не пугать
    // юзера красным в логе при штатном fallback на стриминг.
    return null;
  }
};

// ─── СИНХРОНИЗАЦИЯ ────────────────────────────────────────────────────────────
// Supabase отдаёт не больше 1000 строк за запрос (настройка max rows), поэтому
// всё читаем страницами — иначе карточки сверх 1000 «пропадали».
export const fetchAllRows = async (makeQuery) => {
  const out = [];
  for (let from = 0; ; ) {
    const { data, error } = await makeQuery().range(from, from + 999);
    if (error) throw error;
    if (!data || !data.length) break;
    out.push(...data);
    from += data.length;
  }
  return out;
};

const SRS_FIELDS = ["srs_state", "srs_interval", "srs_ease", "srs_due", "srs_stability", "srs_difficulty", "srs_reps", "srs_lapses", "srs_last_review"];

// Какой SRS-прогресс свежее: по времени последнего повторения (раньше сравнивали
// srs_due — и «не знаю» на устройстве, дающее более ранний due, проигрывало серверу).
const localSrsIsNewer = (lc, sc) => {
  const lt = Date.parse(lc.srs_last_review || "") || 0;
  const st = Date.parse(sc.srs_last_review || "") || 0;
  if (lt || st) return lt > st;
  return (lc.srs_reps || 0) > (sc.srs_reps || 0);
};

export const syncWithSupabase = async (userId) => {
  const online = await isOnline();
  if (!online) return { success: false, reason: "offline" };
  try {
    const { data: sd, error: se } = await supabase.auth.getSession();
    if (se || !sd?.session) return { success: false, reason: "no_session" };
    const exAt = sd.session.expires_at;
    if (exAt && exAt - Math.floor(Date.now() / 1000) < 60) {
      const { error: re } = await supabase.auth.refreshSession();
      if (re) return { success: false, reason: "refresh_failed" };
    }

    // 1. Сначала отправляем всё, что накопилось на устройстве.
    migrateSrsQueue();
    await flushOutbox();

    // 2. Забираем серверные данные целиком (постранично).
    const serverProjs = await fetchAllRows(() => supabase.from("projects").select("*").order("updated_at", { ascending: false }).order("id"));
    const ids = serverProjs.map(p => p.id);
    const serverCards = [];
    // Фильтр по project_id кусками: длинный список id не влезает в один URL.
    for (let i = 0; i < ids.length; i += 100) {
      const chunk = ids.slice(i, i + 100);
      serverCards.push(...await fetchAllRows(() => supabase.from("cards").select("*").in("project_id", chunk).order("id")));
    }
    const byProj = {};
    serverCards.forEach(c => { (byProj[c.project_id] = byProj[c.project_id] || []).push(c); });

    // 3. Сливаем с локальными данными. То, что ещё лежит в очереди отправки,
    //    остаётся локальным; SRS-прогресс берём более свежий.
    const pending = pendingIds();
    const localProjs0 = await loadLocalProjects(userId);
    const localCardMap = {};
    const localProjMap = {};
    localProjs0.forEach(p => {
      localProjMap[p.id] = p;
      (p.cards || []).forEach(c => { localCardMap[c.id] = { ...c, project_id: c.project_id || p.id }; });
    });

    const enriched = serverProjs.map(proj => {
      const merged = [];
      const seen = new Set();
      for (const sc of byProj[proj.id] || []) {
        seen.add(String(sc.id));
        const lc = localCardMap[sc.id];
        if (pending.cards.has(String(sc.id))) { if (lc) merged.push(lc); continue; } // локальная правка ещё в пути (или удаление)
        if (lc && localSrsIsNewer(lc, sc)) {
          const srs = {};
          SRS_FIELDS.forEach(f => { if (lc[f] !== undefined) srs[f] = lc[f]; });
          merged.push({ ...sc, ...srs });
          // Прогресс с устройства не дошёл до сервера (старая версия теряла его) — дошлём.
          enqueue("cards.upsert", "card:" + sc.id, { id: sc.id, project_id: proj.id, ...srs });
          continue;
        }
        merged.push(sc);
      }
      // Новые карточки с устройства, которых сервер ещё не видел.
      for (const lc of Object.values(localCardMap)) {
        if (lc.project_id === proj.id && !seen.has(String(lc.id)) && pending.cards.has(String(lc.id))) merged.push(lc);
      }
      const lp = localProjMap[proj.id];
      let mergedProj = { ...proj, cards: merged, cardCount: merged.length };
      if (lp && pending.projects.has(String(proj.id))) {
        mergedProj = { ...mergedProj, name: lp.name, folder: lp.folder ?? null, src_lang: lp.src_lang ?? mergedProj.src_lang };
      }
      // src_lang: если сервер не отдал значение (колонки нет / null), берём локальное.
      if (!mergedProj.src_lang && lp?.src_lang) mergedProj.src_lang = lp.src_lang;
      if (lp?.local_audio_filename && !mergedProj.local_audio_filename) mergedProj.local_audio_filename = lp.local_audio_filename;
      return mergedProj;
    });
    await Promise.all(enriched.map(proj => saveProjectLocal(proj, proj.cards)));
    // Проекты, удалённые на сервере (например, с другого устройства), убираем и тут.
    try {
      const serverIds = new Set(ids);
      await Promise.all(localProjs0.filter(p => !serverIds.has(p.id)).map(p => deleteProjectLocal(p.id)));
    } catch (e) { console.warn('[Sync] stale cleanup:', e); }
    flushOutbox().catch(() => {}); // дошлём «лечебные» SRS-правки из шага 3
    if (isCapacitor()) {
      Promise.all(enriched.filter(p => p.audio_path).map(async proj => {
        try {
          const { data: { publicUrl } } = supabase.storage.from("audio-files").getPublicUrl(proj.audio_path);
          const filename = await ensureAudioDownloaded(proj.id, publicUrl);
          if (filename && filename !== proj.local_audio_filename) await saveProjectLocal({ ...proj, local_audio_filename: filename }, proj.cards);
        } catch {}
      })).catch(() => {});
    }
    return { success: true, projects: enriched };
  } catch (e) { console.error("[Sync]", e); return { success: false, reason: e.message }; }
};
// ─── SRS ОФЛАЙН-ОЧЕРЕДЬ ───────────────────────────────────────────────────────
// Когда интернет недоступен, SRS-прогресс пишем в очередь.
// При восстановлении связи — отправляем в Supabase.
const SRS_QUEUE_KEY = "ac_srs_queue";

// Офлайн-прогресс теперь идёт через общую очередь отправки (outbox.js).
// pushSrsQueue оставлен для старых вызовов; старую очередь ac_srs_queue
// переносим в outbox при первой синхронизации.
export const srsRow = (projId, c) => ({
  id: c.id,
  project_id: projId,
  srs_state: c.srs_state || "new",
  srs_interval: c.srs_interval || 0,
  srs_ease: c.srs_ease || 2.5,
  srs_due: c.srs_due || null,
  srs_stability:   Number.isFinite(c.srs_stability)  ? c.srs_stability  : null,
  srs_difficulty:  Number.isFinite(c.srs_difficulty) ? c.srs_difficulty : null,
  srs_reps:        c.srs_reps   || 0,
  srs_lapses:      c.srs_lapses || 0,
  srs_last_review: c.srs_last_review || null,
});

export const pushSrsQueue = (projId, cards) => {
  cards.filter(c => c.srs_state || c.srs_due).forEach(c => enqueue("cards.upsert", "card:" + c.id, srsRow(projId, c)));
};

export const getSrsQueue = () => {
  try { return JSON.parse(localStorage.getItem(SRS_QUEUE_KEY) || "[]"); } catch { return []; }
};

const migrateSrsQueue = () => {
  const q = getSrsQueue();
  if (!q.length) return;
  q.forEach(e => enqueue("cards.upsert", "card:" + e.cardId, srsRow(e.projId, { ...e, id: e.cardId })));
  try { localStorage.removeItem(SRS_QUEUE_KEY); } catch {}
};

export const clearSrsQueue = () => { try { localStorage.removeItem(SRS_QUEUE_KEY); } catch {} };

// Отправить накопленные изменения (старая очередь + outbox).
export const flushSrsQueue = async () => {
  migrateSrsQueue();
  const r = await flushOutbox();
  return { flushed: r.sent, error: r.error || undefined };
};

// ─── Синхронизация ежедневной статистики (per-language) ──────────────────────
// Стратегия: per-(lang, date) delta sync.
//   baseline = что было отправлено на сервер при прошлом sync (ac_stats_synced)
//              shape: { [`${lang}|${date}`]: {words_read, listen_sec, ...} }
//   delta    = текущее - baseline  (только положительный прирост)
//   сервер   = atomically adds delta (PostgreSQL function add_daily_stats)
//   после    = тянем актуальные значения с сервера → перезаписываем localStorage + baseline
// Это корректно работает с нескольких устройств: телефон(fr) +30мин + ПК(fr) +20мин = 50мин.
//
// Совместимость: ключи ac_*_by_lang имеют форму { [lang]: { [date]: value } }.
// Если в localStorage остались старые non-per-lang ключи (ac_words_read и т.п.),
// мы их разово сливаем в lang='' бакет — пользователь не теряет накопленную историю.
const STATS_SYNCED_KEY      = 'ac_stats_synced_by_lang';
const STATS_LEGACY_FLAG_KEY = 'ac_stats_legacy_migrated';

const _lsJson = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch { return fallback; } };
const _lsSetJson = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

const ACT_KEY_LANG   = 'ac_activity_by_lang';
const TIME_KEY_LANG  = 'ac_time_by_lang';
const WORDS_KEY_LANG = 'ac_words_read_by_lang';

// One-time migration: if old non-per-lang keys exist, fold them into the
// lang='' bucket so the user keeps their history when sync first runs.
const _migrateLegacyStats = () => {
  if (localStorage.getItem(STATS_LEGACY_FLAG_KEY) === '1') return;
  const oldWords = _lsJson('ac_words_read', null);
  const oldTime  = _lsJson('ac_time',       null);
  const oldAct   = _lsJson('ac_activity',   null);
  if (oldWords || oldTime || oldAct) {
    const words = _lsJson(WORDS_KEY_LANG, {}); if (!words['']) words[''] = {};
    const time  = _lsJson(TIME_KEY_LANG,  {}); if (!time['']) time[''] = {};
    const act   = _lsJson(ACT_KEY_LANG,   {}); if (!act['']) act[''] = {};
    if (oldWords) Object.entries(oldWords).forEach(([d, v]) => {
      words[''][d] = Math.max(words[''][d] || 0, Number(v) || 0);
    });
    if (oldTime)  Object.entries(oldTime).forEach(([d, v]) => {
      const cur = time[''][d] || { listen: 0, read: 0 };
      time[''][d] = {
        listen: Math.max(cur.listen, Number(v?.listen) || 0),
        read:   Math.max(cur.read,   Number(v?.read)   || 0),
      };
    });
    if (oldAct)   Object.entries(oldAct).forEach(([d, v]) => {
      act[''][d] = Math.max(act[''][d] || 0, Number(v) || 0);
    });
    _lsSetJson(WORDS_KEY_LANG, words);
    _lsSetJson(TIME_KEY_LANG,  time);
    _lsSetJson(ACT_KEY_LANG,   act);
  }
  try { localStorage.setItem(STATS_LEGACY_FLAG_KEY, '1'); } catch {}
};

const _key = (lang, date) => `${lang}|${date}`;

export const syncDailyStats = async (userId) => {
  if (!userId) return { ok: false, error: 'no userId' };
  try {
    _migrateLegacyStats();

    // 1) Текущие локальные данные (форма: {lang: {date: value}})
    const wordsByLang = _lsJson(WORDS_KEY_LANG, {});
    const timeByLang  = _lsJson(TIME_KEY_LANG,  {});
    const actByLang   = _lsJson(ACT_KEY_LANG,   {});

    // 2) Baseline — что уже было отправлено при прошлом синке
    const baseline = _lsJson(STATS_SYNCED_KEY, {});

    // 3) Собираем дельты по всем (lang, date) парам, что есть локально
    const seen = new Set();
    const collect = (byLang) => {
      Object.entries(byLang || {}).forEach(([lang, byDate]) => {
        Object.keys(byDate || {}).forEach(date => seen.add(_key(lang, date)));
      });
    };
    collect(wordsByLang); collect(timeByLang); collect(actByLang);

    const deltaRows = [];
    seen.forEach(k => {
      const [lang, date] = k.split('|');
      const b = baseline[k] || { words_read: 0, listen_sec: 0, read_sec: 0, cards_reviewed: 0, listen_focused_sec: 0 };
      const curW   = Number(wordsByLang[lang]?.[date] || 0);
      const curT   = timeByLang[lang]?.[date] || {};
      const curL   = Number(curT.listen          || 0);
      const curR   = Number(curT.read            || 0);
      const curLF  = Number(curT.listen_focused  || 0);
      const curA   = Number(actByLang[lang]?.[date] || 0);
      const dW  = Math.max(0, curW  - b.words_read);
      const dL  = Math.max(0, curL  - b.listen_sec);
      const dR  = Math.max(0, curR  - b.read_sec);
      const dLF = Math.max(0, curLF - (b.listen_focused_sec || 0));
      const dA  = Math.max(0, curA  - b.cards_reviewed);
      if (dW || dL || dR || dLF || dA) {
        deltaRows.push({
          date, lang,
          words_read: dW, listen_sec: dL, read_sec: dR,
          cards_reviewed: dA, listen_focused_sec: dLF,
        });
      }
    });

    // 4) PUSH: атомарное накопление на сервере. RPC `add_daily_stats` принимает
    //    JSONB-массив строк и делает ON CONFLICT DO UPDATE с суммированием.
    if (deltaRows.length) {
      const { error: rpcErr } = await supabase.rpc('add_daily_stats', {
        p_user_id: userId,
        p_rows: deltaRows,
      });
      if (rpcErr) throw rpcErr;
    }

    // 5) PULL: тянем все актуальные значения с сервера. После merge сервер —
    //    единственный источник правды; локально перезаписываем целиком.
    const { data: serverRows, error: pullErr } = await supabase
      .from('user_daily_stats')
      .select('date,lang,words_read,listen_sec,read_sec,cards_reviewed,listen_focused_sec')
      .eq('user_id', userId);
    if (pullErr) throw pullErr;

    // 6) Перепаковка серверных строк в {lang: {date: ...}} и обновление baseline
    const newWords = {}, newTime = {}, newAct = {}, newBaseline = {};
    (serverRows || []).forEach(r => {
      const lang = r.lang || '';
      const date = r.date;
      if (r.words_read) {
        if (!newWords[lang]) newWords[lang] = {};
        newWords[lang][date] = r.words_read;
      }
      if (r.listen_sec || r.read_sec || r.listen_focused_sec) {
        if (!newTime[lang]) newTime[lang] = {};
        newTime[lang][date] = {
          listen:         r.listen_sec         || 0,
          read:           r.read_sec           || 0,
          listen_focused: r.listen_focused_sec || 0,
        };
      }
      if (r.cards_reviewed) {
        if (!newAct[lang]) newAct[lang] = {};
        newAct[lang][date] = r.cards_reviewed;
      }
      newBaseline[_key(lang, date)] = {
        words_read:         r.words_read         || 0,
        listen_sec:         r.listen_sec         || 0,
        read_sec:           r.read_sec           || 0,
        cards_reviewed:     r.cards_reviewed     || 0,
        listen_focused_sec: r.listen_focused_sec || 0,
      };
    });

    _lsSetJson(WORDS_KEY_LANG,  newWords);
    _lsSetJson(TIME_KEY_LANG,   newTime);
    _lsSetJson(ACT_KEY_LANG,    newAct);
    _lsSetJson(STATS_SYNCED_KEY, newBaseline);
    // detail.source = 'sync' so the App-level debounced auto-push listener can
    // ignore this event and not re-schedule itself in an infinite loop.
    try { window.dispatchEvent(new CustomEvent('ac:activity-changed', { detail: { source: 'sync' } })); } catch {}

    return { ok: true, rowsPushed: deltaRows.length, rowsPulled: (serverRows || []).length };
  } catch (e) {
    console.warn('[syncDailyStats]', e.message);
    // Detect missing-schema case so the UI can surface a clearer hint than
    // a generic error — same pattern as text_projects/user_words missing.
    const msg = (e.message || '').toLowerCase();
    if (msg.includes('column') && msg.includes('lang')) {
      return { ok: false, error: e.message, reason: 'schema_missing' };
    }
    if (msg.includes('function') && msg.includes('add_daily_stats')) {
      return { ok: false, error: e.message, reason: 'rpc_missing' };
    }
    return { ok: false, error: e.message };
  }
};

// Wipe one language on the server. Used by the StatsScreen clear-stats flow
// AFTER the user confirms — so they don't lose cross-device history that the
// local-only clear would otherwise re-sync back into existence on next pull.
export const clearLangStatsRemote = async (userId, lang) => {
  if (!userId) return { ok: false };
  try {
    const { error } = await supabase.rpc('clear_daily_stats', {
      p_user_id: userId,
      p_lang:    lang || '',
    });
    if (error) throw error;
    // Drop baseline entries for this lang so a subsequent push doesn't re-send
    // the cleared deltas as if they were new activity.
    const baseline = _lsJson(STATS_SYNCED_KEY, {});
    const prefix = `${lang || ''}|`;
    Object.keys(baseline).forEach(k => { if (k.startsWith(prefix)) delete baseline[k]; });
    _lsSetJson(STATS_SYNCED_KEY, baseline);
    return { ok: true };
  } catch (e) {
    console.warn('[clearLangStatsRemote]', e.message);
    return { ok: false, error: e.message };
  }
};

// ─── Custom languages (cross-device sync) ────────────────────────────────────
// User-defined language list. Local copy lives in localStorage (see
// utils.js getCustomLangs/saveCustomLangs), authoritative copy lives in
// Supabase table `custom_langs`. The pattern is local-first: writes apply
// immediately to localStorage, then are pushed to Supabase. If push fails
// (offline / network error), the op is queued in `ac_custom_langs_queue`
// and replayed on the next sync.
const CUSTOM_LANGS_KEY = 'ac_custom_langs';
const CUSTOM_LANGS_QUEUE_KEY = 'ac_custom_langs_queue';

const _emitCustomLangsChanged = () => {
  try { window.dispatchEvent(new Event('ac_custom_langs_changed')); } catch {}
};

const _readLocalCustomLangs = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(CUSTOM_LANGS_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const _writeLocalCustomLangs = (list) => {
  try {
    localStorage.setItem(CUSTOM_LANGS_KEY, JSON.stringify(list));
    _emitCustomLangsChanged();
  } catch {}
};

const _readQueue = () => _lsJson(CUSTOM_LANGS_QUEUE_KEY, []);
const _writeQueue = (q) => _lsSetJson(CUSTOM_LANGS_QUEUE_KEY, q);
const _enqueue = (op) => {
  const q = _readQueue();
  // Dedupe by code: a later op for the same code supersedes earlier ones.
  const filtered = q.filter(o => o.code !== op.code);
  filtered.push({ ...op, ts: Date.now() });
  _writeQueue(filtered);
};

// ── PUSH (single op) ─────────────────────────────────────────────────────────
const _pushUpsertRemote = async (userId, { code, flag, name }) => {
  if (!userId) return { ok: false, reason: 'no_user' };
  const { error } = await supabase.from('custom_langs').upsert({
    user_id: userId,
    code,
    flag: flag || '🌐',
    name: name || code.toUpperCase(),
    deleted: false,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,code' });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
};

const _pushDeleteRemote = async (userId, code) => {
  if (!userId) return { ok: false, reason: 'no_user' };
  // Soft delete via the `deleted` flag so other devices learn to remove their
  // local copy on next pull. (Hard delete works too but loses cross-device
  // intent if a device pulls before noticing the absence.)
  const { error } = await supabase.from('custom_langs').upsert({
    user_id: userId,
    code,
    deleted: true,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id,code' });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
};

// ── PULL (full reconcile) ────────────────────────────────────────────────────
// Fetch all non-deleted rows from Supabase and merge into local. Server is
// authoritative for what exists; local-only entries (still queued) survive.
export const pullCustomLangsRemote = async (userId) => {
  if (!userId) return { ok: false, reason: 'no_user' };
  try {
    const { data, error } = await supabase
      .from('custom_langs')
      .select('code,flag,name,deleted,updated_at')
      .eq('user_id', userId);
    if (error) throw error;
    const remote = (data || []);
    const queue = _readQueue();
    const queuedCodes = new Set(queue.map(o => o.code));

    // Keep local-queued ops intact; for everything else, server wins.
    const local = _readLocalCustomLangs();
    const merged = [];
    const seen = new Set();

    // 1. Remote items (non-deleted) — they go in unless a local delete is queued.
    remote.forEach(r => {
      if (r.deleted) return;
      const localQueuedDelete = queue.find(o => o.code === r.code && o.kind === 'delete');
      if (localQueuedDelete) return;
      merged.push({ code: r.code, flag: r.flag || '🌐', name: r.name || r.code.toUpperCase() });
      seen.add(r.code);
    });

    // 2. Local items that were never in remote (queued adds) — keep them.
    local.forEach(l => {
      if (seen.has(l.code)) return;
      if (queuedCodes.has(l.code)) merged.push(l);
    });

    _writeLocalCustomLangs(merged);
    return { ok: true, count: merged.length };
  } catch (e) {
    console.warn('[pullCustomLangsRemote]', e.message);
    return { ok: false, error: e.message };
  }
};

// ── FLUSH QUEUE ──────────────────────────────────────────────────────────────
// Replay any pending ops to the server, dropping ones that succeed.
export const flushCustomLangsQueue = async (userId) => {
  if (!userId) return { ok: false, reason: 'no_user' };
  const q = _readQueue();
  if (q.length === 0) return { ok: true, count: 0 };
  const remaining = [];
  let done = 0;
  for (const op of q) {
    const res = op.kind === 'delete'
      ? await _pushDeleteRemote(userId, op.code)
      : await _pushUpsertRemote(userId, op);
    if (res.ok) done += 1;
    else remaining.push(op); // keep for next attempt
  }
  _writeQueue(remaining);
  return { ok: true, count: done, pending: remaining.length };
};

// ── PUBLIC: called by App.js when user adds/removes a custom language ────────
// These wrap utils.js addCustomLang/removeCustomLang with background Supabase
// sync. They never throw — offline becomes a queued op.
export const syncAddCustomLang = async (userId, { code, flag, name }) => {
  // localStorage was already updated by utils.addCustomLang; we just sync.
  if (!userId) { _enqueue({ kind: 'upsert', code, flag, name }); return { ok: false, queued: true }; }
  const res = await _pushUpsertRemote(userId, { code, flag, name });
  if (!res.ok) { _enqueue({ kind: 'upsert', code, flag, name }); return { ok: false, queued: true, error: res.error }; }
  return { ok: true };
};

export const syncRemoveCustomLang = async (userId, code) => {
  if (!userId) { _enqueue({ kind: 'delete', code }); return { ok: false, queued: true }; }
  const res = await _pushDeleteRemote(userId, code);
  if (!res.ok) { _enqueue({ kind: 'delete', code }); return { ok: false, queued: true, error: res.error }; }
  return { ok: true };
};

// Expose queue length for the manual sync UI (orange-dot indicator).
export const getCustomLangsQueueLength = () => _readQueue().length;